import { MAX_UPLOAD_BYTES, MAX_VIDEO_BYTES } from "@/api/client";

/** What the stage accepts: a photograph is identified once, a video frame by frame. */
export type UploadKind = "photo" | "video";

export const ACCEPT = "image/*,video/*";

/** Browsers leave `type` empty for some containers (e.g. .mkv), so fall back to the name. */
const VIDEO_EXTENSIONS = /\.(?:mp4|m4v|mov|webm|mkv|avi|3gp)$/i;

function kindOf(file: File): UploadKind | null {
  if (file.type.startsWith("image/")) return "photo";
  if (file.type.startsWith("video/")) return "video";
  return !file.type && VIDEO_EXTENSIONS.test(file.name) ? "video" : null;
}

const megabytes = (bytes: number) => (bytes / 1024 / 1024).toFixed(1);

/** The kind of a dropped or chosen file, or the reason it can't be sent. */
export function checkUpload(file: File): { kind: UploadKind } | { problem: string } {
  const kind = kindOf(file);
  if (kind === null) {
    return {
      problem:
        `“${file.name}” is neither a photograph nor a video. Use a JPEG, PNG, WebP or HEIC ` +
        "photo, or an MP4, MOV or WebM video.",
    };
  }
  if (kind === "photo" && file.size > MAX_UPLOAD_BYTES) {
    return {
      problem:
        `The photograph is ${megabytes(file.size)} MB; the limit is 20 MB. ` +
        "Export a smaller copy and retry.",
    };
  }
  if (kind === "video" && file.size > MAX_VIDEO_BYTES) {
    return {
      problem:
        `The video is ${megabytes(file.size)} MB; the limit is 200 MB. ` +
        "Trim it to the passes where the shark is close and retry.",
    };
  }
  return { kind };
}
